import { ApiService } from "Services/ApiService";
import { ResultsDTO } from "Types/ResultsDTO";

export interface ClientReview {
  id: number;
  customerId: string | null;
  customerName?: string | null;
  rating: number;
  reviewComment: string;
  createdDate: string;
}

export interface ReviewSummary {
  averageRating: number;
  totalReviews: number;
}

export const ProductReviewClientApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getProductReviewsClient: builder.query<
      ResultsDTO<{ averageRating: number; reviews: any[] }>,
      { plant: string; productId: string }
    >({
      query: ({ plant, productId }) => ({
        url: "/review-rating/get",
        method: "GET",
        params: { plant, productId },
      }),
    }),

    getAllProductRatings: builder.query<Record<number, number>, { plant: string }>({
      query: ({ plant }) => ({
        url: "/review-rating/getAll",
        method: "GET",
        params: { plant },
      }),
      transformResponse: (response: ResultsDTO<{ averageRatings: Record<string, number> }>) => {
        const avgRatings = response?.results?.averageRatings;
        if (!avgRatings || typeof avgRatings !== "object") return {};
        return Object.entries(avgRatings).reduce(
          (acc: Record<number, number>, [productId, rating]) => {
            acc[Number(productId)] = Number(rating);
            return acc;
          },
          {}
        );
      },
    }),

    createProductReview: builder.mutation<ResultsDTO, {
      plant: string;
      productId: string;
      rating: number;
      reviewComment: string;
    }>({
      query: (body) => ({
        url: "/review-rating/create",
        method: "POST",
        body,
      }),
    }),

    // CLIENT editing their own review (rating + comment only). Separate from
    // Admin/ProductReviewAdminApiSlice's updateProductReview (which is used by
    // the admin panel to toggle showHide) so each can authenticate with the
    // correct JWT - both hit the same backend URL, but one needs the CLIENT
    // token and the other the ADMIN token.
    updateProductReviewClient: builder.mutation<ResultsDTO, {
      id: number;
      plant: string;
      rating: number;
      reviewComment: string;
    }>({
      query: (body) => ({
        url: "/review-rating/update",
        method: "PUT",
        body,
      }),
    }),
  }),
});

export const {
  useGetProductReviewsClientQuery,
  useGetAllProductRatingsQuery,
  useCreateProductReviewMutation,
  useUpdateProductReviewClientMutation,
} = ProductReviewClientApiSlice;
